/**
 * Cookie helpers cross-subdomain (.runnaflow.com).
 *
 * Antes (até 2026-04-25, finding A21): readCookie/writeCookie/deleteCookie
 * estavam duplicados em src/lib/supabase.ts e src/contexts/AuthContext.tsx
 * com diferenças sutis (writeCookie do supabase.ts hardcoded em max-age=1 ano,
 * do AuthContext parametrizado). Centralizado aqui.
 */

export function cookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return undefined
  if (host.endsWith('.runnaflow.com') || host === 'runnaflow.com') return '.runnaflow.com'
  return undefined
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${encodeURIComponent(name)}=`
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length))
      } catch {
        return null
      }
    }
  }
  return null
}

/**
 * Escreve cookie. Default `maxAgeSec` = 1 dia.
 * Pra session-mirror cross-subdomain (que precisa coincidir com
 * refresh-token TTL do Supabase), passar 1 ano = `60 * 60 * 24 * 365`.
 */
export function writeCookie(name: string, value: string, maxAgeSec = 60 * 60 * 24): boolean {
  if (typeof document === 'undefined') return false
  try {
    const attrs = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      'path=/',
      `max-age=${maxAgeSec}`,
      'SameSite=Lax',
    ]
    if (window.location.protocol === 'https:') attrs.push('Secure')
    const d = cookieDomain()
    if (d) attrs.push(`domain=${d}`)
    document.cookie = attrs.join('; ')
    return true
  } catch {
    return false
  }
}

export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  try {
    const attrs = [`${encodeURIComponent(name)}=`, 'path=/', 'max-age=0']
    const d = cookieDomain()
    if (d) attrs.push(`domain=${d}`)
    document.cookie = attrs.join('; ')
  } catch {
    /* ignore */
  }
}
